import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { inventoryActions } from "redux/Ldb/actions";
import jsonToCsvExport from "json-to-csv-export";
import moment from "moment";
import { calculateStockoutDate } from "utils/calculate-stockoutdate";
import { formatMoney } from "helpers/formatMoney";

const useExportProductList = ({ total = 0 }: { total: number }) => {
    const dispatch = useAppDispatch();
    const [productList, setProductList] = useState([]);
    const [fetchingProductList, setFetchingProductList] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const limit = 50;
    const [startExport, setStartExport] = useState(false);
    const [finishedExport, setFinishedExport] = useState(false);
    const { exportProductListSuccess } = useAppSelector((state) => state.inventory);

    useEffect(() => {
        if (total > 0 && startExport) {
            setFetchingProductList(true);
            setFinishedExport(false);
            dispatch(
                inventoryActions.exportProductList(limit, page, "Finished Goods", true, search)
            );
        }
    }, [dispatch, page, search, startExport, total]);

    useEffect(() => {
        if (Boolean(exportProductListSuccess)) {
            setProductList((prev) =>
                uniqBy([...prev, ...exportProductListSuccess.inventory?.data], "_id")
            );
            if (Math.ceil(total / limit) === page) {
                setFetchingProductList(false);
                setStartExport(false);
                setFinishedExport(true);
                setPage(1);
            } else {
                setPage((prev) => prev + 1);
            }
        }
        return () => {
            dispatch(inventoryActions.resetExportProductList());
        };
    }, [exportProductListSuccess]);

    useEffect(() => {
        if (finishedExport && productList.length > 0) {
            const exportData = productList.map((product) => {
                const givenDate = moment(product?.forcastData?.stockoutDate);
                const today = moment().startOf("day");
                let differenceInDays = 0;

                if (givenDate.isValid()) {
                    differenceInDays = givenDate.diff(today, "days");
                }

                const reorderDate = calculateStockoutDate(
                    product?.forcastData?.stockoutDate,
                    product?.leadTime
                );
                const differenceInDaysInReorderDate = reorderDate?.diff(today, "days");

                return {
                    "Product name": product?.productName,
                    "SKU No.": product?.sku,

                    Categories: product?.inventoryCategory,
                    Channel:
                        product?.channels?.length === 0
                            ? "Not available"
                            : product?.channels?.map((channel) => {
                                  return channel?.toLowerCase()?.includes("synth")
                                      ? "Retail"
                                      : channel?.toLowerCase()?.includes("trackstar")
                                      ? channel?.split(".")[1]
                                      : channel;
                              }),
                    "Current stock level": `${
                        product?.productStockDetails?.stockLevel?.toLocaleString() || "0"
                    }${` `}${product?.productStockDetails?.unitOfMeasurement || ""}`,
                    "Forecasted needs": `${
                        product?.forcastData?.forecastedNeeds?.toLocaleString() || "0"
                    }${` `}${product?.productStockDetails?.unitOfMeasurement || ""}`,
                    "On order": product?.onOrder?.toLocaleString() || "0",
                    "Re-order before": !product?.forcastData?.incidentType
                        ? "Not Available"
                        : product?.forcastData?.incidentType?.toLowerCase() !== "stockout"
                        ? "Overstocked"
                        : // : product?.forcastData?.incidentType?.toLowerCase() === "stockout" ||
                        differenceInDays > 0 && differenceInDays <= 7
                        ? moment().format("MMM DD, YYYY")
                        : differenceInDays < 0
                        ? "Not Available"
                        : moment(product?.forcastData?.stockoutDate).format("MMM DD, YYYY"),
                    "Stockout date": `${reorderDate?.format("MMM DD, YYYY") || "Not Available"} (${
                        reorderDate
                            ? differenceInDaysInReorderDate === 0
                                ? "Today"
                                : formatMoney().format(differenceInDaysInReorderDate) + " days"
                            : "Not Available"
                    })`,
                };
            });

            jsonToCsvExport({
                data: exportData,
                filename: "inventory_list",
            });
            setFinishedExport(false);
            setPage(1);
        }
    }, [finishedExport, productList]);

    return { fetchingProductList, productList, setPage, setSearch, setStartExport };
};

export default useExportProductList;
